import "react-dates/initialize";

import React, { useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import {
  useForm,
  useFieldArray,
  Controller,
  FormProvider,
} from "react-hook-form";

import moment from "moment";

import {
  RadioButtonGroup,
  RadioButton,
  Select,
  SelectItem,
  Blockquote,
  Modal,
} from "@wfp/ui";

import {
  buildAttendanceUrl,
  getFilteredAttendanceTotal,
} from "helpers/attendance";
import {
  getEnrolmentAggregateOnDate,
  getOverEnrolmentEntries,
} from "helpers/enrolment";

import { getSchoolYearByDate, updateStore } from "data-handler/ducks/stores";
import commoditiesDuck from "data-handler/ducks/commodities";
import {
  getCurrentSchoolProfileWfpCommodities,
  getCurrentSchoolProfileOtherCommodities,
} from "data-handler/ducks/schools";
import { getSchoolYearLevelsBySchoolYearId } from "data-handler/ducks/stores";

import { getFoodSources } from "data-handler/ducks/foodSources";
import { RepeaterItem } from "components/Repeater";
import CommoditySelect from "components/CommoditySelect";
import QuantityInput from "components/QuantityInput";
import RepeaterAddButton from "components/RepeaterAddButton";
import ErrorList from "components/ErrorList";
import {
  validationResolver,
  requiredLabelCommodities,
} from "components/MealsEdit";
import TaheHomeRationsWizard from "components/TakeHomeRations/TaheHomeRationsWizard";
import {
  attendanceDesktop,
  attendanceLevelData,
  attendanceRowsDesktop,
  attendanceTotalsRowDesktop,
} from "components/AttendanceEdit";
import EnrolmentWarningModal from "components/EnrolmentWarningModal";

import {
  studentKinds,
  takeHomeRationCategory,
  nonWfpCategory,
} from "SCConstants";

import { getStockStatus } from "helpers/stock";

import "./_content.scss";

const enrichStockEvent = (stockEvent) => {
  const category =
    stockEvent.category || stockEvent.meta?.category || takeHomeRationCategory;
  return {
    ...stockEvent,
    category,
    sign: -1,
    type: "CONS",
    target: "DEL",
    side_effects: stockEvent.side_effects || [],
    meta: { ...(stockEvent.meta || {}), category },
  };
};

const storeToFormValues = (store) => {
  const consumption = store.consumption
    ? JSON.parse(JSON.stringify(store.consumption))
    : {};
  const stock_events = (consumption.stock_events || []).map((se) => ({
    ...se,
    source: typeof se.source === "object" ? se.source?.id : se.source,
  }));
  const attendanceValues = JSON.parse(JSON.stringify(store));
  if (attendanceValues.levels) {
    attendanceValues.levels.forEach((level) => {
      studentKinds.forEach((kind) => {
        // cast to str (may become `"undefined"` - no problem for formValuesToStore)
        level[kind.value] = `${level[kind.value]}`;
      });
    });
  }
  return { stock_events, ...attendanceValues };
};

const formValuesToStore = (values) => {
  if (values.stock_events) {
    values.category = takeHomeRationCategory;
  }
  if (values.levels) {
    values.levels.forEach((level) => {
      studentKinds.forEach((kind) => {
        // cast to int
        level[kind.value] = parseInt(level[kind.value]) || 0;
      });
    });
  }

  return { ...values };
};

const formValuesToConsumption = (values) => {
  const stock_events = (values.stock_events || []).map(enrichStockEvent);
  return { stock_events };
};

const TakeHomeRations = ({
  children,
  currentStoreData,
  currentSchoolProfile,
}) => {
  const intl = useIntl();

  const commodityListOther = useSelector(
    getCurrentSchoolProfileOtherCommodities
  );
  const commoditiesListWfpFromSchoolProfile = useSelector(
    getCurrentSchoolProfileWfpCommodities
  );

  const foodSources = useSelector(getFoodSources);
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();

  const newDate = currentStoreData?.occurred_on
    ? moment(currentStoreData?.occurred_on)
    : params.thrItem && params.thrItem.split("+").length === 2
    ? moment(params.thrItem.split("+")[1])
    : params.studentItem && params.studentItem.split("+").length === 2
    ? moment(params.studentItem.split("+")[1])
    : undefined;
  const occurredOnDate = newDate.format("YYYY-MM-DD");
  const schoolYear = useSelector(
    getSchoolYearByDate(newDate?.format("YYYY-MM-DD"))
  );

  const schoolYearLevels = useSelector(
    getSchoolYearLevelsBySchoolYearId(schoolYear.object_id)
  );

  const enrolmentSelector = useMemo(
    () => getEnrolmentAggregateOnDate(moment(occurredOnDate)),
    [occurredOnDate]
  );
  const enrolmentAggregate = useSelector(enrolmentSelector);

  const [showEnrolmentWarning, setShowEnrolmentWarning] = useState(false);
  const [overEntries, setOverEntries] = useState([]);
  const [pendingValues, setPendingValues] = useState(null);
  const wizardRef = useRef();

  const stockStatus = useSelector(
    getStockStatus(moment(newDate).format("YYYY-MM-DD"))
  );

  const latestStockStatus = useSelector(
    getStockStatus(moment().format("YYYY-MM-DD"))
  );

  let defaultValues = {
    occurred_on: newDate?.format("YYYY-MM-DD"),
    levels:
      currentStoreData?.levels === undefined
        ? []
        : JSON.parse(JSON.stringify(currentStoreData?.levels)),
    consumption:
      currentStoreData?.consumption === undefined
        ? {}
        : JSON.parse(JSON.stringify(currentStoreData?.consumption)),
  };

  if (schoolYear) {
    // Update defaultValues to account for all current levels and shifts
    schoolYear.levels.forEach((level) => {
      if (
        schoolYear.has_morning_classes &&
        !defaultValues.levels.find(
          (attendanceLevel) =>
            attendanceLevel.level === level.level &&
            attendanceLevel.shift === "morning"
        )
      ) {
        defaultValues.levels.push({ level: level.level, shift: "morning" });
      }
      if (
        schoolYear.has_afternoon_classes &&
        !schoolYear.has_same_students_in_both_shifts &&
        !defaultValues.levels.find(
          (attendanceLevel) =>
            attendanceLevel.level === level.level &&
            attendanceLevel.shift === "afternoon"
        )
      ) {
        defaultValues.levels.push({ level: level.level, shift: "afternoon" });
      }
    });
  }

  // There are thr wfp commodities that have Nonzero values.
  const thrInStock = commoditiesListWfpFromSchoolProfile.some(
    (commodity) =>
      Number(latestStockStatus.takehomeration[commodity.id] || "0") > 0
  );
  // There are purchasedetail wfp commodities that have Nonzero values.
  const purchaseInStock = commoditiesListWfpFromSchoolProfile.some(
    (commodity) =>
      Number(latestStockStatus.purchasedetail[commodity.id] || "0") > 0
  );

  const currentSchoolEnabledPurchases =
    purchaseInStock || currentSchoolProfile.enable_cash_purchases;
  const currentSchoolEnabledTHR =
    thrInStock || currentSchoolProfile.enable_take_home_rations;

  const methods = useForm({
    resolver: validationResolver,
    defaultValues: storeToFormValues(defaultValues),
    context: {
      currentStoreData,
      commodityListOther,
      currentSchoolEnabledPurchases,
      currentSchoolEnabledTHR,
      stockStatus,
    },
  });

  const [page, setPage] = useState(0);
  const [storePreview, setStorePreview] = useState();
  const [formState, setFormState] = useState();

  const previous = () => {
    setPage(Math.max(page - 1, 0));
  };

  const isLastPage = page === React.Children.count(children) - 1;
  const attendanceLabel = intl.formatMessage({
    id: "takeHomeRations.attendanceLabel",
    defaultMessage: "Enter take-home ration participants",
  });

  const mealsLabel = intl.formatMessage({
    id: "takeHomeRations.mealsLabel",
    defaultMessage: "Enter take-home ration consumption",
  });

  const { control, watch, errors, setValue, register, getValues } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "stock_events",
  });

  let previewTimeout;

  const updateStorePreview = () => {
    if (previewTimeout) clearTimeout(previewTimeout);
    previewTimeout = setTimeout(() => {
      setStorePreview({ ...storePreview, ...getValues({ nest: true }) });
    }, 200);
  };

  if (!storePreview) updateStorePreview();

  const showMorningFields = schoolYear
    ? schoolYear.has_morning_classes === true
    : undefined;
  const showAfternoonFields = schoolYear
    ? schoolYear.has_afternoon_classes === true &&
      schoolYear.has_same_students_in_both_shifts !== true
    : undefined;
  const totalsDesktop = attendanceTotalsRowDesktop(
    schoolYear,
    schoolYearLevels,
    showMorningFields,
    showAfternoonFields,
    storePreview
  );

  const rowsDesktop = attendanceRowsDesktop(
    attendanceLevelData(schoolYearLevels),
    storePreview,
    showMorningFields,
    showAfternoonFields,
    defaultValues,
    enrolmentAggregate
  );

  const categoryOptions = [
    {
      value: takeHomeRationCategory,
      name: takeHomeRationCategory,
      label: intl.formatMessage({
        id: "categoryOption.takeHomeRation",
        defaultMessage: "Take-home ration commodity",
      }),
    },
  ];
  const commoditiesIndex = useSelector(commoditiesDuck.getIndex);
  const stockEventsWatch = watch("stock_events");
  const categoryWatch = (category, index) =>
    watch(`stock_events[${index}].category`) === category;

  const totalStudents = getFilteredAttendanceTotal({
    attendance: storePreview,
  });

  const id = (currentStoreData && currentStoreData.client_id) || undefined;

  const closeModal = (newId) => {
    history.push(
      buildAttendanceUrl(
        occurredOnDate,
        params.schoolId,
        params.item,
        newId ? newId : id,
        params.studentItem
      )
    );
  };

  const doSubmit = (values) => {
    // TODO: Adjust Sync object based on new Category Models
    const toBeSaved = {
      ...currentStoreData,
      consumption: {
        ...currentStoreData?.consumption,
        ...formValuesToConsumption(values[2]),
        event_type: "consumption",
        occurred_at:
          currentStoreData?.consumption?.occurred_at ||
          defaultValues.occurred_on,
        category: takeHomeRationCategory,
        meal_provided: true,
      },
      ...formValuesToStore(values[1]),
    };

    const store = dispatch(
      updateStore({
        id: id,
        values: toBeSaved,
        occurred_on: defaultValues.occurred_on,
        model: "attendance",
        type: currentStoreData?.client_id ? "update" : "create",
        category: takeHomeRationCategory,
      })
    );

    if (!store.blocked) {
      closeModal(store.data.id);
    }
  };

  const onSubmit = (values) => {
    doSubmit(values);
  };

  const handleAttemptNext = (values, currentPage) => {
    if (currentPage === 0) {
      const entries = getOverEnrolmentEntries(
        values?.levels,
        enrolmentAggregate
      );
      if (entries.length > 0) {
        setOverEntries(entries);
        setPendingValues(values);
        setShowEnrolmentWarning(true);
        return false;
      }
    }
    return true;
  };

  if (fields.length === 0) {
    append({});
  }

  return (
    <>
      <Modal
        open
        hideClose
        onPreviousClick={previous}
        onRequestClose={() => {}}
        onSecondarySubmit={() => closeModal(params.thrItem)}
        previousHidden={page > 0 ? false : true}
        nextHidden={isLastPage}
        submitHidden={!isLastPage}
        modalLabel={
          <FormattedMessage
            id="AttendanceEdit.thrAttendanceModalLabel"
            defaultMessage="Take-home ration data"
          />
        }
        secondaryButtonText={intl.formatMessage({
          id: "AttendanceEdit.cancel",
          defaultMessage: "Cancel",
        })}
        modalHeading={`${moment(defaultValues.occurred_on).format(
          "dddd, DD.MM.YYYY"
        )}`}
        wide
        selectorPrimaryFocus={false}
        className="thr-modal"
      >
        <TaheHomeRationsWizard
          ref={wizardRef}
          methods={methods}
          onSubmit={onSubmit}
          onAttemptNext={handleAttemptNext}
          setFormState={setFormState}
          formState={formState}
          nextDisabled={!process.env.REACT_APP_E2E && totalStudents === 0}
        >
          {
            // First page, thr attendance
          }
          <TaheHomeRationsWizard.Page label={attendanceLabel}>
            <FormProvider {...methods}>
              <form onChange={updateStorePreview} className="attendance-edit">
                {totalStudents === 0 && (
                  <Blockquote kind="warning">
                    <FormattedMessage
                      id="info.addParticipantsBeforeProceedingWarning"
                      defaultMessage="You need to add participants before proceeding to the next page."
                    />
                  </Blockquote>
                )}
                <Controller as={<input type="hidden" />} name={`occurred_on`} />
                {defaultValues.levels.map((level, i) => (
                  /** Hidden fields to preserve level and shift info when submitting */
                  <>
                    <Controller
                      as={<input type="hidden" />}
                      name={`levels[${i}].level`}
                    />
                    <Controller
                      as={<input type="hidden" />}
                      name={`levels[${i}].shift`}
                    />
                  </>
                ))}
                <div className="row">
                  {attendanceDesktop(
                    showMorningFields,
                    showAfternoonFields,
                    rowsDesktop,
                    totalsDesktop
                  )}
                </div>
              </form>
            </FormProvider>
          </TaheHomeRationsWizard.Page>
          {
            //Second Page, thr meals
          }
          <TaheHomeRationsWizard.Page label={mealsLabel}>
            <FormProvider {...methods}>
              <form onChange={updateStorePreview} className="attendance-edit">
                <>
                  {totalStudents === 0 && (
                    <Blockquote kind="warning">
                      <FormattedMessage
                        id="info.noAttendance"
                        defaultMessage="You haven't inputted attendance data therefore we can't
                  populate the total quantity field according to the quantity
                  per student automatically."
                      />
                    </Blockquote>
                  )}
                  <ErrorList errors={errors} />

                  <div>
                    {fields.map((field, index) => (
                      <RepeaterItem
                        key={field.id}
                        removeAction={() => remove(index)}
                        disableRemove={fields.length === 1}
                      >
                        <div>
                          <>
                            <RadioButtonGroup
                              labelText={
                                <FormattedMessage
                                  id="MealsEdit.commoditySource"
                                  defaultMessage="Commodity Source"
                                />
                              }
                            >
                              {categoryOptions.map((category) => (
                                <RadioButton
                                  key={category.value}
                                  labelText={category.label}
                                  id={`stock_events[${index}][${category.value}]`}
                                  name={`stock_events[${index}].category`}
                                  value={category.value}
                                  defaultChecked={
                                    category.value === takeHomeRationCategory
                                  }
                                  onChange={(e) => {
                                    setValue(
                                      `stock_events[${index}].category`,
                                      e.target.value,
                                      {
                                        shouldDirty: true,
                                      }
                                    );
                                    setValue(
                                      `stock_events[${index}].commodity`,
                                      null,
                                      {
                                        shouldDirty: true,
                                      }
                                    );
                                  }}
                                  ref={
                                    currentSchoolEnabledPurchases
                                      ? register({ required: true })
                                      : register({ required: false })
                                  }
                                />
                              ))}
                            </RadioButtonGroup>
                          </>
                        </div>
                        <Controller
                          render={({ onChange, value }) => (
                            <CommoditySelect
                              category={watch(
                                `stock_events[${index}].category`
                              )}
                              onChange={(e) => {
                                setValue(
                                  `stock_events[${index}].measure_unit`,
                                  e.measure_unit
                                );
                                onChange(e.value, e.category);
                              }}
                              value={value}
                              showStock={true}
                              invalidOverride={
                                errors[`stock_events[${index}].commodity`] !==
                                undefined
                              }
                              invalidTextOverride={requiredLabelCommodities}
                              date={currentStoreData?.occurred_on}
                            />
                          )}
                          name={`stock_events[${index}].commodity`}
                          control={control}
                        />
                        {
                          // Only show `source` field when selected commodity is not from WFP
                          stockEventsWatch &&
                            stockEventsWatch[index] &&
                            stockEventsWatch[index].commodity &&
                            !commoditiesIndex[stockEventsWatch[index].commodity]
                              ?.is_wfp &&
                            categoryWatch(nonWfpCategory, index) && (
                              <Controller
                                className="meals__source"
                                defaultValue={foodSources[0].id}
                                as={
                                  <Select
                                    labelText={
                                      <FormattedMessage
                                        id="MealsEdit.source"
                                        defaultMessage="Source"
                                      />
                                    }
                                  >
                                    {foodSources.map((foodSource) => (
                                      <SelectItem
                                        key={foodSource.id}
                                        value={foodSource.id}
                                        text={foodSource.source}
                                      />
                                    ))}
                                  </Select>
                                }
                                name={`stock_events[${index}].source`}
                                placeholder=""
                                control={control}
                              />
                            )
                        }

                        <Controller
                          render={({ onChange, onBlur, value }) => {
                            return (
                              <QuantityInput
                                control={control}
                                value={value}
                                onChange={(e) => {
                                  if (totalStudents) {
                                    setValue(
                                      `stock_events[${index}].quantity`,
                                      parseFloat(
                                        e.target.value * totalStudents
                                      ).toFixed(2),
                                      { shouldDirty: true }
                                    );
                                  }
                                  return onChange(e);
                                }}
                                onBlur={onBlur}
                                labelText={
                                  <FormattedMessage
                                    id="MealsEdit.quantityPerStudents"
                                    defaultMessage="Quantity per student"
                                  />
                                }
                                setValue={setValue}
                                name={`stock_events[${index}].quantityPerStudent`}
                                invalid={
                                  errors[
                                    `stock_events[${index}].quantityPerStudent`
                                  ]
                                }
                                invalidText={
                                  errors[
                                    `stock_events[${index}].quantityPerStudent`
                                  ]?.message
                                }
                                measureUnit={watch(
                                  `stock_events[${index}].measure_unit`
                                )}
                              />
                            );
                          }}
                          control={control}
                          name={`stock_events[${index}].quantityPerStudent`}
                        />

                        <Controller
                          render={({ onChange, onBlur, value }) => {
                            return (
                              <QuantityInput
                                control={control}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                labelText={
                                  <FormattedMessage
                                    id="MealsEdit.TotalQuantity"
                                    defaultMessage="Total quantity"
                                  />
                                }
                                setValue={setValue}
                                name={`stock_events[${index}].quantity`}
                                invalid={
                                  errors[`stock_events[${index}].quantity`]
                                }
                                invalidText={
                                  errors[`stock_events[${index}].quantity`]
                                    ?.message
                                }
                                measureUnit={watch(
                                  `stock_events[${index}].measure_unit`
                                )}
                              />
                            );
                          }}
                          control={control}
                          name={`stock_events[${index}].quantity`}
                        />

                        <Controller
                          name={`stock_events[${index}].measure_unit`}
                          control={control}
                        />
                      </RepeaterItem>
                    ))}
                  </div>
                  <RepeaterAddButton onClick={() => append({})} />
                </>
              </form>
            </FormProvider>
          </TaheHomeRationsWizard.Page>
        </TaheHomeRationsWizard>
      </Modal>
      <EnrolmentWarningModal
        open={showEnrolmentWarning}
        overEntries={overEntries}
        showShift={showAfternoonFields}
        onCancel={() => setShowEnrolmentWarning(false)}
        onConfirm={() => {
          setShowEnrolmentWarning(false);
          if (pendingValues && wizardRef.current) {
            wizardRef.current.advance(pendingValues);
          }
        }}
      />
    </>
  );
};

export default TakeHomeRations;
